import { useCallback, useEffect, useRef, useState } from 'react'
import maplibregl from 'maplibre-gl'
import {
	DEFAULT_CENTER,
	DEFAULT_ZOOM,
	MAP_STYLE,
	MAX_ZOOM,
	MIN_ZOOM,
	SITE_ZOOM,
	SATELLITE_AVAILABLE,
	SATELLITE_LAYER_ID,
	STREET_LAYER_ID,
	readStoredBasemap,
	storeBasemap,
	type Basemap,
} from '../config/mapConfig'

export interface UseMapLibreOptions {
	center?: [number, number]
	zoom?: number
	onMapLoad?: (map: maplibregl.Map) => void
	/**
	 * What this surface opens on when the user has expressed no preference.
	 * A remembered choice always wins. Defaults to 'street'.
	 */
	defaultBasemap?: Basemap
	/**
	 * The basemap this map MUST show, ignoring and never storing the user's
	 * remembered preference (MYD-500). For a presentation slide, where the
	 * author chose the basemap and two viewers must see the same slide.
	 * Satellite falls back to street where no imagery key is configured.
	 */
	fixedBasemap?: Basemap
	/** Initial rotation and tilt, for a framed camera. Default 0. */
	bearing?: number
	pitch?: number
	/**
	 * False for a static map: no pan, zoom, rotate or keyboard handling, so a
	 * slide map never captures the wheel or a tap meant to advance the slide
	 * (MYD-500). Default true.
	 */
	interactive?: boolean
	/** False skips the Navigation and Scale controls. Default true. */
	showControls?: boolean
	/**
	 * Keep the canvas readable after drawing, which a snapshot's rasteriser
	 * needs (Phase 5). Off by default: it costs memory and speed.
	 */
	preserveDrawingBuffer?: boolean
}

export interface UseMapLibreReturn {
	map: maplibregl.Map | null
	isLoaded: boolean
	flyTo: (center: [number, number], zoom?: number) => void
	fitBounds: (bounds: maplibregl.LngLatBoundsLike, padding?: number) => void
	setContainer: (el: HTMLDivElement | null) => void
	/** 'street' when no satellite key is configured. */
	basemap: Basemap
	setBasemap: (basemap: Basemap) => void
}

/**
 * Show one raster layer and hide the other.
 *
 * Visibility rather than `setStyle`, which would discard every source and layer
 * the overlay components add imperatively after load. See the note on MAP_STYLE.
 */
const applyBasemap = (map: maplibregl.Map, basemap: Basemap): void => {
	if (!SATELLITE_AVAILABLE) return
	if (!map.getLayer(STREET_LAYER_ID) || !map.getLayer(SATELLITE_LAYER_ID)) return
	map.setLayoutProperty(STREET_LAYER_ID, 'visibility', basemap === 'street' ? 'visible' : 'none')
	map.setLayoutProperty(SATELLITE_LAYER_ID, 'visibility', basemap === 'satellite' ? 'visible' : 'none')
}

export function useMapLibre(options: UseMapLibreOptions = {}): UseMapLibreReturn {
	const {
		center,
		zoom,
		onMapLoad,
		defaultBasemap = 'street',
		fixedBasemap,
		bearing,
		pitch,
		interactive = true,
		showControls = true,
		preserveDrawingBuffer = false,
	} = options
	const [container, setContainer] = useState<HTMLDivElement | null>(null)
	const mapRef = useRef<maplibregl.Map | null>(null)
	const [isLoaded, setIsLoaded] = useState(false)
	// Read once, on mount: re-reading would fight the user's own switching.
	const [storedBasemap, setBasemapState] = useState<Basemap>(() => readStoredBasemap(defaultBasemap))
	const basemap: Basemap = fixedBasemap ? (fixedBasemap === 'satellite' && SATELLITE_AVAILABLE ? 'satellite' : 'street') : storedBasemap
	const basemapRef = useRef(basemap)
	basemapRef.current = basemap
	const onLoadRef = useRef(onMapLoad)
	onLoadRef.current = onMapLoad
	const initialCenterRef = useRef(center)
	const initialZoomRef = useRef(zoom)
	// Construction options are read once: changing them means a new map.
	const constructionRef = useRef({ bearing, pitch, interactive, showControls, preserveDrawingBuffer })

	useEffect(() => {
		if (!container) return
		const construction = constructionRef.current

		const map = new maplibregl.Map({
			container,
			style: MAP_STYLE,
			center: initialCenterRef.current ?? DEFAULT_CENTER,
			zoom: initialZoomRef.current ?? DEFAULT_ZOOM,
			bearing: construction.bearing ?? 0,
			pitch: construction.pitch ?? 0,
			minZoom: MIN_ZOOM,
			maxZoom: MAX_ZOOM,
			interactive: construction.interactive,
			// A static map shows its attribution in full: there is no button to
			// click to expand it on a slide, and OSM requires it to be visible.
			attributionControl: { compact: construction.interactive },
			...(construction.preserveDrawingBuffer ? { canvasContextAttributes: { preserveDrawingBuffer: true } } : {}),
		})

		if (construction.showControls) {
			map.addControl(new maplibregl.NavigationControl({ showCompass: true }), 'top-right')
			map.addControl(new maplibregl.ScaleControl({ unit: 'imperial' }), 'bottom-left')
		}

		map.on('load', () => {
			mapRef.current = map
			// Before isLoaded flips, so a remembered Satellite preference is
			// already applied the first time the map paints rather than flashing
			// the street map and swapping underneath the user.
			applyBasemap(map, basemapRef.current)
			setIsLoaded(true)
			onLoadRef.current?.(map)
			map.resize()
		})

		const resizeObserver = new ResizeObserver(() => {
			map.resize()
		})
		resizeObserver.observe(container)

		return () => {
			resizeObserver.disconnect()
			setIsLoaded(false)
			mapRef.current = null
			map.remove()
		}
	}, [container])

	const flyTo = useCallback((c: [number, number], z?: number) => {
		mapRef.current?.flyTo({ center: c, zoom: z ?? SITE_ZOOM })
	}, [])

	const fitBounds = useCallback(
		(bounds: maplibregl.LngLatBoundsLike, padding: number = 60) => {
			mapRef.current?.fitBounds(bounds, { padding, maxZoom: SITE_ZOOM })
		},
		[]
	)

	// A fixed basemap can change after mount (the author picks another in the
	// inspector); follow it without rebuilding the map.
	useEffect(() => {
		if (fixedBasemap && isLoaded && mapRef.current) applyBasemap(mapRef.current, basemap)
	}, [fixedBasemap, basemap, isLoaded])

	const setBasemap = useCallback(
		(next: Basemap) => {
			// A fixed basemap is the author's, not the viewer's: never stored.
			if (fixedBasemap) return
			setBasemapState(next)
			storeBasemap(next)
			if (mapRef.current) applyBasemap(mapRef.current, next)
		},
		[fixedBasemap],
	)

	return { map: mapRef.current, isLoaded, flyTo, fitBounds, setContainer, basemap, setBasemap }
}
